/* How big the picture is allowed to be.
 *
 * This has now been got wrong three times, each time by trading crispness for
 * area and each time measurably:
 *   - a half-step candidate that is crisp at no device-pixel ratio at all,
 *     handing the commonest iPhone widths 4.5 device pixels per game pixel;
 *   - a budget of an eighth, then a twentieth, of the picture — at dpr 3 the
 *     crisp steps are 1/3 apart, so any budget under 22% rejects every one of
 *     them, and eight of twelve common phone geometries ran at 2.80, 3.50,
 *     4.38, 4.42, 4.64 or 4.85 device pixels per game pixel;
 *   - rounding the device-pixel ratio, which turned a Pixel 8's real 2.625
 *     into 3 and produced a "crisp" 3.5.
 *
 * A pixel game whose pixels come in two sizes is not a smaller picture, it is
 * a broken one. There is no budget: the scale lands on a whole device pixel or
 * the maths is wrong. The area comes back out of the chrome instead.
 */
export const VW = 256, VH = 216;   // 192 of world under a 24-row strip (2026-09-27, LS-475): the plate was 72 rows, a quarter of the picture

/** The device-pixel ratio to quantise against: the real one, clamped, never
 *  rounded. Fractional ratios are ordinary on Android. */
export const usableDpr = (dpr) => Math.min(4, Math.max(1, dpr || 1));

/** The largest scale at or below what fits that puts a whole number of device
 *  pixels in a game pixel. Falls back to the raw fit only when even one device
 *  pixel per game pixel does not fit — a window smaller than 256x240. */
export function pickScale(availW, availH, rawDpr) {
  const dpr = usableDpr(rawDpr);
  const raw = Math.min(availW / VW, availH / VH);
  const crisp = Math.floor(raw * dpr) / dpr;
  return crisp >= 1 / dpr ? crisp : raw;
}

/* WHAT THIS COSTS, MEASURED ACROSS EIGHT GEOMETRIES, AND WHY IT STANDS.
 *
 * On a 375x667 phone at dpr 2 the raw fit is 1.465 and the crisp candidates
 * are 1.0 and 1.5. 1.5 needs 384 CSS pixels of width and the phone has 375 —
 * nine short — so the picture is 256 CSS px wide, 68% of the width and 24.6%
 * of the screen, and a 5x7 glyph is about 1.1mm tall. Two reviewers have both
 * called it the top layout defect.
 *
 * The suggested fix is a budget: take crisp when it is cheap, else take the
 * raw fit with the backing store at whole device pixels. It does not work.
 * Final device pixels per game pixel is `(cssWidth / VW) * dpr`, and cssWidth
 * is bounded by the viewport — so a non-crisp scale is a non-crisp scale
 * whatever the backing store is sized to, and nearest-neighbour at 2.93 is
 * game pixels three device pixels wide in most columns and two in one of
 * every fourteen. A budget was tried twice (1/8, then 1/20) and both times it
 * was this same shimmer with a number in front of it.
 *
 * The chrome is not the lever either: WIDTH binds on every phone in portrait
 * and the vertical slack goes unused, so smaller controls buy nothing.
 *
 * That left `VW`, and `VW` has now been measured rather than argued. Dropping
 * the playfield from 16 tiles to 15 (VW 256 -> 240) over eight real phone
 * geometries:
 *
 *   phone                256x240        240x240
 *   iPhone SE / 8        24.6%          51.8%   <- the case everyone names
 *   iPhone 12/13/14      33.2%          31.1%
 *   iPhone 14 Pro Max    42.6%          39.9%
 *   Pixel 5              38.9%          36.4%
 *   Pixel 8              37.8%          35.5%
 *   Galaxy S21           37.9%          35.6%
 *   Galaxy A51           37.9%          35.5%
 *   iPad mini portrait   45.6%          61.5%
 *   mean                 37.3%          40.9%
 *
 * (% of the screen the picture covers, controls taking a third of the height.)
 *
 * It helps two geometries enormously and makes five of the eight WORSE, and
 * the reason is quantisation: at dpr 3 a narrower picture lands on the same
 * 4/3 step it already had, so it buys no scale and simply shows less world.
 * The two that improve are the ones where 240 crosses a step 256 cannot reach
 * (1.0 -> 1.5 at dpr 2, 2.5 -> 3.0 on the iPad).
 *
 * So the trade is: one column of the world, five phones smaller, two much
 * bigger. That is not a defect with a fix, it is a choice with no dominant
 * answer, and it belongs to whoever decides how wide a screen of this game is
 * — not to this function. The picture is smaller than it could be on one
 * device class, on purpose, because a pixel game whose pixels come in two
 * sizes is a broken one. The checks in test/soak.mjs fail if a budget comes
 * back instead, and now also if any of these eight geometries stops being
 * crisp.
 */
